/**
 * deform_layer — the GROUND's read of the deformation field (shader gaps program round 3, weather surfaces,
 * 2026-09-30). The field (`lib/deform_field.ts`, the engine heightmap) lives in the rain-shelter atlas's lower
 * region; this is how the v2 ground turns a depth into an imprint without a sampler of its own (the ground is at
 * its sampled-texture limit — every tap here is the SAME binding as the roof map's).
 *
 * NO DISPLACEMENT: a near chunk is 5.3 m between vertices (seg 48 over 256 m); a boot print is 26 cm. The imprint is
 * Barré-Brisebois' console path — the dent's SLOPE bends the normal (four taps, the engine's corrected sign: the +x
 * wall of a depression leans back toward its centre), the hollow is occluded, compacted snow is a little greyer and
 * smoother, a mud rut darker and, in rain, holds water. How much of a depth shows is the SURFACE's: snow all of it,
 * sand most, wet soil some, a road, rock or gravel none.
 *
 * Every tap is explicit LOD 0 (`deformDepthAt`), legal inside the ground's non-uniform branches; the colour Fn never
 * reads `normalWorld` (THE ORDER LAW) — only the depth and the positions it is handed.
 */
import { float, vec3, mix, clamp, smoothstep, max } from 'three/tsl';
import { deformDepthAt, deformBoundsNode } from './rain_shelter.js';
import { DEFORM_MAX_DEPTH_M } from './deform_field.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL graphs are loosely typed, as in wetness.ts
type N = any;

/** The depth (m) at which a dent reads fully compacted. */
export const DEFORM_FULL_M = 0.06;

export interface DeformSoftness {
  /** Snow on the ground: the weather's accumulation or the snow layer's weight. */
  snow: N;
  /** Soft soil: loam / turf / litter / mud weights. */
  soil: N;
  /** Sand: dune / beach weights. */
  sand: N;
  /** Hard ground: road / rock / strata / gravel weights (they take no print). */
  hard: N;
  /** The local wetness (the rain on the soil). */
  wet: N;
}

export interface DeformImprint {
  /** A world-space horizontal normal perturbation to add to the geometry normal (−∂h/∂x, 0, −∂h/∂z). */
  pert: N;
  /** How deep the dent reads, 0..1 (visible depth / DEFORM_FULL_M). */
  amount: N;
  /** Whether the dent is snow (1) or soil (0) — its look. */
  snowish: N;
  /** Colour → colour. */
  shade: (col: N) => N;
  /** Roughness multiplier. */
  rough: N;
  /** Indirect occlusion multiplier. */
  ao: N;
  /** Standing water in a rut (0..1) — a puddle term. */
  rutWater: N;
}

/** The imprint at a RENDER-space ground point. */
export function deformImprint(wx: N, wz: N, s: DeformSoftness): DeformImprint {
  const snowS: N = clamp(s.snow, 0, 1);
  const soilS: N = clamp(s.soil, 0, 1).mul(mix(float(0.25), float(1), smoothstep(float(0.15), float(0.7), s.wet)));
  const sandS: N = clamp(s.sand, 0, 1).mul(0.7);
  const soft: N = clamp(max(max(snowS, soilS), sandS).mul(float(1).sub(clamp(s.hard, 0, 1).mul(0.9))), 0, 1).toVar('gDfSoft');
  const b: N = deformBoundsNode;
  const c: N = b.w; // the cell (m)
  const d0: N = deformDepthAt(wx, wz).toVar('gDf0');
  const dL: N = deformDepthAt(wx, wz, c.negate() as never, 0), dR: N = deformDepthAt(wx, wz, c as never, 0);
  const dD: N = deformDepthAt(wx, wz, 0, c.negate() as never), dU: N = deformDepthAt(wx, wz, 0, c as never);
  // the surface is y = −depth·soft: its normal ∝ (∂depth/∂x, 1, ∂depth/∂z)·soft — the engine's corrected sign
  const inv: N = soft.div(c.mul(2));
  const pert: N = vec3(dR.sub(dL).mul(inv), 0, dU.sub(dD).mul(inv)).toVar('gDfPert');
  const vis: N = d0.mul(soft);
  const amount: N = clamp(vis.div(DEFORM_FULL_M), 0, 1).toVar('gDfAmt');
  const snowish: N = clamp(snowS.div(max(snowS.add(soilS).add(sandS), float(1e-3))), 0, 1);
  const rutWater: N = amount.mul(float(1).sub(snowish)).mul(clamp(s.soil, 0, 1)).mul(smoothstep(float(0.3), float(0.8), s.wet));
  return {
    pert, amount, snowish, rutWater,
    // compacted snow: a touch greyer (packed crystals scatter less); a rut: darker earth
    shade: (col: N) => mix(col, col.mul(mix(float(0.78), float(0.86), snowish)), amount),
    // packed snow is smoother; a fresh rut's walls rougher unless water fills it
    rough: mix(float(1), mix(float(1.08), float(0.72), snowish), amount),
    ao: float(1).sub(amount.mul(0.35)),
  };
}

/** The deepest a dent can be, for the harness's sanity check against the field's own cap. */
export const DEFORM_LAYER_MAX_M = DEFORM_MAX_DEPTH_M;
